import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, inputCls, btnPrimary, EmptyState } from "../../components/bits";
import { Plus, Search } from "lucide-react";
import { OppCard } from "../../components/opp/OppCard";
import { OppFormModal } from "../../components/opp/OppFormModal";
import { OppAttsModal } from "../../components/opp/OppAttsModal";
import { OppPartsModal } from "../../components/opp/OppPartsModal";
import { OppStaffPartsModal } from "../../components/opp/OppStaffPartsModal";

const emptyOpp = {
  name: "", description: "", location: "", start_date: "", end_date: "",
  category: "عامة", seats: 0, status: "open", level: "عام (28 ر.س/ساعة)", volunteer_type: "الجميع", hours: 0,
  main_value: "", supporting_values: [], expected_impact: "",
};

export default function Opportunities() {
  const [opps, setOpps] = useState(null);
  const [values, setValues] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isSec, setIsSec] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyOpp);
  const [parts, setParts] = useState([]);
  const [staffParts, setStaffParts] = useState([]);
  const [partsTab, setPartsTab] = useState("students");
  const [partForm, setPartForm] = useState({ student_id: "", staff_id: "", hours: 2 });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");

  const load = useCallback(() => api.get("/portal/opportunities").then((r) => setOpps(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => {
    load();
    api.get("/portal/values").then((r) => setValues(r.data)).catch(() => {});
    api.get("/portal/settings").then(async (r) => {
      const sec = (r.data.school?.stage || "ثانوية") === "ثانوية";
      setIsSec(sec);
      if (sec) {
        const [st, sf] = await Promise.all([api.get("/portal/students"), api.get("/portal/staff")]);
        setStudents(st.data);
        setStaffList(sf.data);
      } else {
        const { data } = await api.get("/portal/staff");
        setStaffList(data);
      }
    }).catch(() => {
      api.get("/portal/students").then((r) => setStudents(r.data)).catch(() => {});
    });
  }, [load]);

  const refreshAtts = async () => {
    const { data } = await api.get("/portal/opportunities");
    setOpps(data);
    setModal((m) => ({ ...m, opp: data.find((x) => x.id === m.opp.id) || m.opp }));
  };

  const uploadAtt = async (kind, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("حجم الملف يتجاوز 3 ميجابايت");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/portal/opportunities/${modal.opp.id}/attachments/${kind}`, fd);
      toast.success("تم رفع المرفق");
      await refreshAtts();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const removeAtt = async (kind) => {
    try {
      await api.delete(`/portal/opportunities/${modal.opp.id}/attachments/${kind}`);
      toast.success("تم حذف المرفق");
      await refreshAtts();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (modal?.mode === "edit") {
        await api.put(`/portal/opportunities/${modal.opp.id}`, form);
        toast.success("تم تحديث الفرصة");
      } else {
        await api.post("/portal/opportunities", form);
        toast.success("تمت إضافة الفرصة التطوعية");
      }
      setModal(null);
      setForm(emptyOpp);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`حذف الفرصة «${o.name}» وجميع مشاركاتها؟`)) return;
    try {
      await api.delete(`/portal/opportunities/${o.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const activeType = isSec ? partsTab : "staff";

  const openParts = async (o) => {
    setPartsTab((t) => (modal?.opp?.id === o.id ? t : "students"));
    setModal({ mode: "parts", opp: o });
    const jobs = [api.get(`/portal/opportunities/${o.id}/staff-participants`).then((r) => setStaffParts(r.data))];
    if (isSec) jobs.unshift(api.get(`/portal/opportunities/${o.id}/participants`).then((r) => setParts(r.data)));
    await Promise.all(jobs);
  };

  const addPart = async () => {
    const isStudents = activeType === "students";
    const selId = isStudents ? partForm.student_id : partForm.staff_id;
    if (!selId) {
      toast.error(isStudents ? "اختر الطالب أولاً" : "اختر الموظف أولاً");
      return;
    }
    setBusy(true);
    try {
      if (isStudents) {
        await api.post(`/portal/opportunities/${modal.opp.id}/participants`, partForm);
      } else {
        await api.post(`/portal/opportunities/${modal.opp.id}/staff-participants/bulk`, { staff_ids: [selId], hours: partForm.hours });
      }
      toast.success("تم تسجيل المشاركة");
      setPartForm({ student_id: "", staff_id: "", hours: 2 });
      openParts(modal.opp);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const bulkAddParts = async (ids) => {
    if (!ids.length) return;
    const isStudents = activeType === "students";
    setBusy(true);
    try {
      const r = await api.post(
        isStudents ? `/portal/opportunities/${modal.opp.id}/participants/bulk` : `/portal/opportunities/${modal.opp.id}/staff-participants/bulk`,
        isStudents ? { student_ids: ids, hours: partForm.hours, date: partForm.date } : { staff_ids: ids, hours: partForm.hours });
      toast.success(`تم تسجيل ${r.data.added} ${isStudents ? "طالباً" : "موظفاً"}${r.data.skipped ? ` (تُخطّي ${r.data.skipped} مسجلين مسبقاً)` : ""}`);
      openParts(modal.opp);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const updatePart = async (pid, hours, studentId) => {
    try {
      await api.put(activeType === "students" ? `/portal/participations/${pid}` : `/portal/staff-participations/${pid}`,
        activeType === "students" ? { student_id: studentId, hours, date: "" } : { hours });
      toast.success("حُدّثت الساعات");
      openParts(modal.opp);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removePart = async (pid) => {
    try {
      await api.delete(activeType === "students" ? `/portal/participations/${pid}` : `/portal/staff-participations/${pid}`);
      openParts(modal.opp);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!opps) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;
  const filtered = opps.filter((o) => (!q || o.name.includes(q) || (o.location || "").includes(q)) && (!statusF || o.status === statusF));

  const partsTabs = (
    <div data-testid="parts-type-tabs" className="flex gap-1.5 p-1 bg-bg rounded-xl w-fit">
      <button data-testid="parts-tab-students" onClick={() => setPartsTab("students")}
        className={`rounded-lg px-4 py-1.5 text-[12px] font-bold transition-all ${partsTab === "students" ? "bg-coral-600 text-white shadow" : "text-mutext hover:bg-surface"}`}>
        الطلاب ({parts.length})
      </button>
      <button data-testid="parts-tab-staff" onClick={() => setPartsTab("staff")}
        className={`rounded-lg px-4 py-1.5 text-[12px] font-bold transition-all ${partsTab === "staff" ? "bg-coral-600 text-white shadow" : "text-mutext hover:bg-surface"}`}>
        المنسوبون ({staffParts.length})
      </button>
    </div>
  );

  return (
    <div data-testid="opportunities-page">
      <PageHeader title="الفرص التطوعية" sub="كل فرصة مرتبطة بقيمة رئيسية وقيم مساندة وأثر متوقع وفق قيم الوزارة الست">
        <button data-testid="opportunity-add-btn" onClick={() => { setForm(emptyOpp); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> فرصة جديدة
        </button>
      </PageHeader>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-3 text-mutext" />
          <input data-testid="opp-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث باسم الفرصة أو الموقع…" className={`${inputCls} pr-9`} />
        </div>
        <select data-testid="opp-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} className={`${inputCls} !w-40`}>
          <option value="">كل الحالات</option>
          <option value="planned">مخطط لها</option>
          <option value="open">مفتوحة</option>
          <option value="done">منفذة</option>
          <option value="cancelled">ملغاة</option>
        </select>
      </div>

      {opps.length === 0 ? (
        <EmptyState testid="opportunities-empty" text="لا توجد فرص بعد — سجّل اليوم فرصة، فقد تكون بداية أثرٍ يدوم" />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((o) => (
            <OppCard key={o.id} o={o}
              onParts={openParts}
              onAtts={(opp) => setModal({ mode: "atts", opp })}
              onEdit={(opp) => { setForm({ ...emptyOpp, ...opp }); setModal({ mode: "edit", opp }); }}
              onDelete={remove} />
          ))}
        </div>
      )}

      <OppFormModal open={modal?.mode === "add" || modal?.mode === "edit"} mode={modal?.mode}
        onClose={() => setModal(null)} form={form} setForm={setForm} values={values} busy={busy} onSave={save} />
      <OppAttsModal open={modal?.mode === "atts"} opp={modal?.opp} onClose={() => setModal(null)}
        busy={busy} onUpload={uploadAtt} onRemove={removeAtt} />
      <OppPartsModal open={isSec && modal?.mode === "parts" && partsTab === "students"} opp={modal?.opp} onClose={() => setModal(null)}
        students={students} parts={parts} partForm={partForm} setPartForm={setPartForm}
        busy={busy} onAdd={addPart} onRemovePart={removePart} onBulkAdd={bulkAddParts} onUpdatePart={updatePart}
        header={partsTabs} otherCount={staffParts.length} />
      <OppStaffPartsModal open={modal?.mode === "parts" && (!isSec || partsTab === "staff")} opp={modal?.opp} onClose={() => setModal(null)}
        staff={staffList} parts={staffParts} partForm={partForm} setPartForm={setPartForm}
        busy={busy} onAdd={addPart} onRemovePart={removePart} onBulkAdd={bulkAddParts} onUpdatePart={updatePart}
        header={isSec ? partsTabs : null} otherCount={isSec ? parts.length : 0} />
    </div>
  );
}
